"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import type { getCurrentUser } from "@/lib/auth";
import LogoutButton from "@/components/LogoutButton";
import { getStoredLeagueId, setStoredLeagueId } from "@/lib/lastLeague";

// defaultLeagueId is resolved server-side (see layout.tsx) so nav links are
// correct on the very first paint. sessionStorage can't be read during SSR,
// so the stored (last-visited) league is synced in an effect after mount and
// takes priority once available — it only ever overrides the default, never
// leaves a page linkless while pending.
function useCurrentLeagueId(
  urlLeagueId: string | null,
  defaultLeagueId: string | null,
): string | null {
  const [storedLeagueId, setStoredLeagueIdState] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (urlLeagueId) {
      setStoredLeagueId(urlLeagueId);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing from sessionStorage, a client-only external system
      setStoredLeagueIdState(urlLeagueId);
    } else {
      setStoredLeagueIdState(getStoredLeagueId());
    }
  }, [urlLeagueId]);

  return urlLeagueId ?? storedLeagueId ?? defaultLeagueId;
}

type CurrentUser = Awaited<ReturnType<typeof getCurrentUser>>;

const NAV_LINKS = [
  { key: "scoreboard", label: "Scoreboard", href: () => "/" },
  {
    key: "lineups",
    label: "Lineups",
    href: () => "/lineups",
  },
  {
    key: "teams",
    label: "Teams",
    href: (leagueId: string | null) =>
      leagueId ? `/leagues/${leagueId}/teams` : "/leagues",
  },
] as const;

const TOOLS_LINKS = [
  {
    key: "report-card",
    label: "Report Card",
    href: () => "/report-card",
  },
  {
    key: "history",
    label: "History",
    href: (leagueId: string | null) =>
      leagueId ? `/leagues/${leagueId}/history` : "/leagues",
  },
  {
    key: "waivers",
    label: "Waivers",
    href: () => "/waivers",
  },
  {
    key: "players",
    label: "Players",
    href: (leagueId: string | null) =>
      leagueId ? `/leagues/${leagueId}/players` : "/leagues",
  },
  {
    key: "rankings",
    label: "Rankings",
    href: () => "/rankings",
  },
  {
    key: "streamers",
    label: "Streamers",
    href: () => "/streamers",
  },
  {
    key: "spy",
    label: "Spy",
    href: () => "/spy",
  },
  {
    key: "offenses",
    label: "Offenses",
    href: () => "/offenses",
  },
  {
    key: "schedule",
    label: "Schedule",
    href: () => "/schedule",
  },
] as const;

export default function Header({
  user,
  defaultLeagueId,
}: {
  user: CurrentUser;
  defaultLeagueId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const pathname = usePathname();

  const router = useRouter();

  const urlMatch = pathname.match(/^\/leagues\/([^/]+)(?:\/|$)/);
  const urlLeagueId = urlMatch ? urlMatch[1] : null;
  const leagueId = useCurrentLeagueId(urlLeagueId, defaultLeagueId);

  // Refreshes cached player stats/values across all seasons/leagues — was
  // previously a per-page "Refresh" button on the Players tab; moved here
  // since the underlying API is admin-only anyway (see
  // /api/cache/player-stats), so it belongs with the rest of the account menu
  // rather than duplicated per page.
  async function refreshData() {
    setRefreshing(true);
    try {
      await Promise.all([
        fetch("/api/cache/player-stats", { method: "POST" }),
        fetch("/api/cache/player-values?source=all", { method: "POST" }),
      ]);
      router.refresh();
    } finally {
      setRefreshing(false);
    }
  }

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (!toolsOpen) return;
    function onClick(e: MouseEvent) {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [toolsOpen]);

  const toolsActive = TOOLS_LINKS.some((t) => pathname === t.href(leagueId));

  // Publishes the header's rendered height as a CSS var so other sticky
  // elements (e.g. the scoreboard's toggle bar) can stick directly below it
  // instead of guessing a fixed offset that'd drift across breakpoints.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setHeight = () =>
      document.documentElement.style.setProperty(
        "--header-height",
        `${el.offsetHeight}px`,
      );
    setHeight();
    const observer = new ResizeObserver(setHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <header
      ref={headerRef}
      className="sticky top-0 z-40 border-b border-zinc-900 bg-zinc-950"
    >
      <div className="relative flex flex-col items-center gap-1.5 px-4 py-3 sm:flex-row sm:justify-center sm:gap-0 sm:px-8 sm:py-5">
        <div className="fixed bottom-4 right-4 z-40 flex justify-center sm:absolute sm:bottom-auto sm:right-8">
          {user ? (
            <div className="relative" ref={ref}>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex h-6 cursor-pointer items-center border border-zinc-700 bg-black px-4 text-sm font-medium text-white transition hover:border-zinc-500 hover:text-white sm:h-auto sm:bg-transparent sm:px-5 sm:py-2"
              >
                {user.username}
              </button>
              {open && (
                <div className="absolute bottom-full right-0 z-10 mb-2 sm:bottom-auto sm:mb-0 sm:pt-2">
                  <div className="w-40 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 py-1 shadow-xl">
                    <Link
                      href="/leagues"
                      onClick={() => setOpen(false)}
                      className="block px-4 py-2 text-sm text-white transition hover:bg-zinc-800 hover:text-white"
                    >
                      Leagues
                    </Link>
                    <Link
                      href="/settings"
                      onClick={() => setOpen(false)}
                      className="block px-4 py-2 text-sm text-white transition hover:bg-zinc-800 hover:text-white"
                    >
                      Settings
                    </Link>
                    {user.role === "admin" && (
                      <Link
                        href="/admin"
                        onClick={() => setOpen(false)}
                        className="block px-4 py-2 text-sm text-white transition hover:bg-zinc-800 hover:text-white"
                      >
                        Admin
                      </Link>
                    )}
                    {user.role === "admin" && (
                      <button
                        type="button"
                        onClick={refreshData}
                        disabled={refreshing}
                        className="block w-full px-4 py-2 text-left text-sm text-white transition hover:bg-zinc-800 hover:text-white disabled:opacity-50"
                      >
                        {refreshing ? "Refreshing…" : "Refresh Data"}
                      </button>
                    )}
                    <LogoutButton className="block w-full px-4 py-2 text-left text-sm text-white transition hover:bg-zinc-800 hover:text-white" />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <Link
              href="/login"
              className="flex h-6 items-center rounded-full border border-zinc-700 px-4 text-sm font-medium text-white transition hover:border-zinc-500 hover:text-white sm:h-auto sm:px-5 sm:py-2"
            >
              Log in
            </Link>
          )}
        </div>
        {user && (
          <nav className="flex flex-wrap justify-center gap-0.5 sm:gap-1">
            {NAV_LINKS.map((t) => {
              const href = t.href(leagueId);
              const active = pathname === href;
              return (
                <Link
                  key={t.key}
                  href={href}
                  className={`shrink-0 rounded-md px-2 py-1 text-xs transition sm:px-3 sm:py-1.5 sm:text-sm ${
                    active
                      ? "bg-zinc-800 font-semibold text-white"
                      : "text-white hover:bg-zinc-900 hover:text-zinc-300"
                  }`}
                >
                  {t.label}
                </Link>
              );
            })}
            <div className="relative" ref={toolsRef}>
              <button
                type="button"
                onClick={() => setToolsOpen((v) => !v)}
                className={`flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs transition sm:px-3 sm:py-1.5 sm:text-sm ${
                  toolsActive
                    ? "bg-zinc-800 font-semibold text-white"
                    : "text-white hover:bg-zinc-900 hover:text-zinc-300"
                }`}
              >
                Tools
                <span className={`text-xs transition ${toolsOpen ? "rotate-180" : ""}`}>▾</span>
              </button>
              {toolsOpen && (
                <div className="absolute left-0 top-full z-30 mt-2 w-40 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 py-1 shadow-xl">
                  {TOOLS_LINKS.map((t) => {
                    const href = t.href(leagueId);
                    const active = pathname === href;
                    return (
                      <Link
                        key={t.key}
                        href={href}
                        onClick={() => setToolsOpen(false)}
                        className={`block px-4 py-2 text-sm transition hover:bg-zinc-800 hover:text-white ${
                          active ? "font-semibold text-white" : "text-white"
                        }`}
                      >
                        {t.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
